import { Injectable } from '@angular/core';
import { Message } from '../interfaces/message';

// Tipos de mensaje que el chatroom pinta; se comparte aqui para que layout
// (prefetch) y chatroom (carga/paginacion) filtren exactamente igual.
export const ALLOWED_MESSAGE_TYPES = [
  'text',
  'interactive',
  'location',
  'image',
  'document',
];

export function filterAllowedMessages(messages: Message[]): Message[] {
  return messages.filter(message =>
    ALLOWED_MESSAGE_TYPES.includes(message.type)
  );
}

// Identifica un mensaje por msgId (WhatsApp) o id (interno); los mensajes
// optimistas (recien enviados, aun sin eco del backend) no tienen ninguno.
export function getMessageKey(message: Message): string | undefined {
  return message.msgId || message.id || undefined;
}

export function isOptimisticMessage(message: Message): boolean {
  return !message.id && !message.msgId;
}

export function isSameMessage(a: Message, b: Message): boolean {
  const key = getMessageKey(b);
  return !!key && (a.msgId === key || a.id === key);
}

export interface CachedConversation {
  messages: Message[];
  page: number;
  loadMoreMessages: boolean;
  lastAccess: number;
}

export interface CachedCustomerDetail {
  saleCount: number;
  lastSaleDate: string;
  detailAddressUser: string;
  lastAccess: number;
}

const MAX_ENTRIES = 30;

// Cache en memoria de historial de mensajes y detalle de cliente, keyed por
// telefono (no por conversationId: el conversationId puede cambiar para el
// mismo telefono cuando el publisher crea una conversacion nueva).
@Injectable({
  providedIn: 'root',
})
export class ConversationCacheService {
  private conversations = new Map<string, CachedConversation>();
  private customerDetails = new Map<string, CachedCustomerDetail>();
  // Evita disparar prefetch duplicados si llegan varios mensajes seguidos
  // de un mismo telefono aun sin entrada en cache
  private pendingPrefetch = new Set<string>();

  get(phone: string): CachedConversation | undefined {
    const entry = this.conversations.get(phone);
    if (entry) {
      entry.lastAccess = Date.now();
    }
    return entry;
  }

  has(phone: string): boolean {
    return this.conversations.has(phone);
  }

  set(phone: string, entry: Omit<CachedConversation, 'lastAccess'>): void {
    this.evictIfNeeded(this.conversations);
    this.conversations.set(phone, { ...entry, lastAccess: Date.now() });
  }

  // Anexa un mensaje a una entrada existente con dedupe por msgId/id; si no
  // hay entrada retorna false para que el llamador decida sembrar la cache
  appendMessage(phone: string, message: Message): boolean {
    const entry = this.conversations.get(phone);
    if (!entry) return false;

    const alreadyExists = entry.messages.some(m => isSameMessage(m, message));
    if (!alreadyExists) {
      entry.messages.push(message);
    }
    entry.lastAccess = Date.now();
    return true;
  }

  getCustomerDetail(phone: string): CachedCustomerDetail | undefined {
    const entry = this.customerDetails.get(phone);
    if (entry) {
      entry.lastAccess = Date.now();
    }
    return entry;
  }

  setCustomerDetail(
    phone: string,
    entry: Omit<CachedCustomerDetail, 'lastAccess'>
  ): void {
    this.evictIfNeeded(this.customerDetails);
    this.customerDetails.set(phone, { ...entry, lastAccess: Date.now() });
  }

  // Intenta tomar el lock de prefetch para este telefono; retorna false si ya
  // habia uno en curso, en cuyo caso el llamador no debe disparar otro
  tryLockPrefetch(phone: string): boolean {
    if (this.pendingPrefetch.has(phone)) return false;
    this.pendingPrefetch.add(phone);
    return true;
  }

  unlockPrefetch(phone: string): void {
    this.pendingPrefetch.delete(phone);
  }

  clear(): void {
    this.conversations.clear();
    this.customerDetails.clear();
    this.pendingPrefetch.clear();
  }

  private evictIfNeeded<T extends { lastAccess: number }>(
    map: Map<string, T>
  ): void {
    if (map.size < MAX_ENTRIES) return;
    let oldestKey: string | null = null;
    let oldestAccess = Infinity;
    for (const [key, value] of map.entries()) {
      if (value.lastAccess < oldestAccess) {
        oldestAccess = value.lastAccess;
        oldestKey = key;
      }
    }
    if (oldestKey !== null) {
      map.delete(oldestKey);
    }
  }
}
